import { FC } from 'react'

import { useAtomValue } from 'jotai'
import { useNavigate } from 'react-router-dom'
import { inflect } from 'inflection'

import {
  Text,
  GreyGrey80,
  Primary13Regular,
  Primary13SemiBold,
  useNotifications,
  SimpleModal
} from '@sequencehq/core-components'

import { useTemplateResolver } from '../../../hooks'
import { TaskOutcome } from '../../types'
import {
  bccAtom,
  bodyAtom,
  ccAtom,
  customerProjectionAtom,
  invoiceCustomerLegalNameAtom,
  invoiceIdAtom,
  invoiceNumberAtom,
  invoiceProjectionAtom,
  subjectAtom,
  toAtom
} from './emailReview.atoms'

export const ModalEmailApprovalResponse: FC<{
  onClose: () => void
  completeTask: (outcome: TaskOutcome) => Promise<boolean>
}> = ({ onClose, completeTask }) => {
  const { displayNotification } = useNotifications()
  const navigate = useNavigate()

  const to = useAtomValue(toAtom)
  const cc = useAtomValue(ccAtom)
  const bcc = useAtomValue(bccAtom)
  const subject = useAtomValue(subjectAtom)
  const body = useAtomValue(bodyAtom)
  const invoiceId = useAtomValue(invoiceIdAtom)
  const invoiceNumber = useAtomValue(invoiceNumberAtom)
  const invoiceCustomerLegalName = useAtomValue(invoiceCustomerLegalNameAtom)
  const invoice = useAtomValue(invoiceProjectionAtom)
  const customer = useAtomValue(customerProjectionAtom)

  const { resolveTemplate } = useTemplateResolver({ invoice, customer })

  const recipientsCount = to.length + cc.length + bcc.length

  return (
    <SimpleModal
      variant="dialog"
      data-testid="watchtower.reviewEmail.modal"
      isOpen
      onClose={onClose}
      title={`Send invoice reminder to ${recipientsCount} ${inflect('recipient', recipientsCount)}`}
      onSubmit={async () => {
        // The send task forwards this output verbatim, so the template
        // variables must be resolved before the task completes
        const res = await completeTask({
          to: to.map(resolveTemplate),
          cc: cc.map(resolveTemplate),
          bcc: bcc.map(resolveTemplate),
          subject: resolveTemplate(subject),
          text: resolveTemplate(body)
        })

        if (!res) {
          displayNotification('Unable to send reminder. Please try again.', {
            type: 'error'
          })
          return false
        }

        displayNotification(`Reminder for ${invoiceNumber} sent`, {
          type: 'success',
          viewLabel: 'View invoice',
          onClickView: () => navigate(`/invoices/${invoiceId}`),
          'data-testid': 'watchtower.reviewEmail.toast'
        })

        return true
      }}
      submitButtonText="Send reminder"
      submittingButtonText="Sending..."
      disableSubmitOnEnter
      dialogPosition="top"
    >
      <Text {...Primary13Regular} color={GreyGrey80}>
        You are about to send a reminder for invoice{' '}
        <Text as="span" {...Primary13SemiBold} color="inherit">
          {invoiceNumber}
        </Text>{' '}
        for customer{' '}
        <Text as="span" {...Primary13SemiBold} color="inherit">
          {invoiceCustomerLegalName}
        </Text>
        .
      </Text>
    </SimpleModal>
  )
}
